import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { UserAnalyticsService } from "@analytics/index";

import { getCompany, getCompanyPatents, getDataSources, listCompanies } from "@api/company.api";

import C from "@constants/constants";

import { IPatentsQuery } from "@interfaces/company.type";

import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";

export const useCompaniesQuery = () => {
  const { filters, pagination, selection, sort } = useTableStore("company");

  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await listCompanies({
        filters,
        pagination,
        sort,
      });

      UserAnalyticsService.trackDirectorySearchResults(
        {
          filters,
          pagination,
          selection,
          sort,
        },
        result?.total || 0,
      );
      return result;
    },
    queryKey: [
      "companies",
      JSON.stringify({
        filters,
        pagination,
        sort,
      }),
    ],
    staleTime: C.STALE_TIME,
  });
};

export const useCompanyQuery = () => {
  const [companyId, setCompanyId] = useState<string>("");
  const [enabled, setEnabled] = useState(false);

  const query = useQuery({
    enabled,
    queryFn: async () => await getCompany(companyId),
    queryKey: ["company", companyId],
    staleTime: C.STALE_TIME,
  });

  const fetchCompany = useCallback((newCompanyId: string) => {
    setCompanyId(newCompanyId);
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (query.status === "error") {
      PopupActions.openNotification({
        content: "There was an issue finding the selected company. Please try again.",
        title: "Oops. This is embarrassing…",
      });
    }
  }, [query.status]);

  return {
    ...query,
    fetchCompany,
  };
};

export const useDataSourcesQuery = (companyId: string) => {
  return useQuery({
    queryFn: async () => {
      return await getDataSources(companyId);
    },
    queryKey: ["dataSources", companyId],
    staleTime: C.STALE_TIME,
  });
};

const defaultSelectedStatus = () => {
  const localStorageItem = localStorage.getItem("selectedStatus");

  if (!localStorageItem) {
    return ["Granted"];
  }

  const statusInLocalStorage = JSON.parse(localStorageItem) as string[];
  if (statusInLocalStorage) {
    return statusInLocalStorage;
  }
  return ["Granted"];
};

export const usePatentsQuery = ({ companyId, field, limit, page, searchId, sort, technologyId }: IPatentsQuery) => {
  const [enableSearch, setEnableSearch] = useState(false);
  const [search, setSearch] = useState("");

  const [patentStatus, setPatentStatus] = useState<string[]>(defaultSelectedStatus());

  const query = useQuery({
    enabled: enableSearch,
    queryFn: async () => {
      return await getCompanyPatents({
        companyId,
        field,
        limit,
        page,
        patentStatus,
        search,
        searchId,
        sort,
        technologyId,
      });
    },
    queryKey: [
      "patents",
      companyId,
      limit,
      page,
      searchId,
      technologyId,
      search,
      field,
      sort,
      patentStatus.sort((a, b) => a.localeCompare(b)).join(","),
    ],
    staleTime: C.STALE_TIME,
  });

  const handleSearch = useCallback((newSearch: string) => {
    setEnableSearch(true);
    setSearch(newSearch);
  }, []);

  const handlePatentStatus = useCallback((newStatus: string[]) => {
    setPatentStatus(newStatus);
  }, []);

  return {
    ...query,
    data: query.data || {
      patents: [],
      totalRows: 0,
    },
    handlePatentStatus,
    handleSearch,
  };
};
